import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './img-spc-dealers-model-za.scss';
import { FMDropdown } from '@own/fds-react';
import { Option } from '@own/fds-react/dist/types/fm-dropdown.types';
import {
    ImgSpcDealerModelDetailsApiDataZAF,
    ImgSpcResultsForDerivativeApiDataData,
    ResultsForDerivativeParams,
} from '../../../../models/serice-price-calculator-model';
import ImgSpcVehicleServiceZa from '../img-spc-vehicle-service-za/img-spc-vehicle-service-za';
import { ComponentType } from '../img-service-price-calculator';
import { ImgMaintenanceServiceCost } from '../img-spc-service-maintenance-cost/img-spc-maintenance-service-cost';
import {
    API_RESPONSE,
    ApiResponseStatus,
} from '../../../../contexts/ford-rides-context/ford-rides-context';
import {
    ImgSpcFieldLabelsContentFragment,
    ImgSpcHeadingContentFragment,
    ImgSpcIolmContentFragment,
    ImgSpcZaContentFragment,
} from '../../../../models/img-service-price-calculator/img-service-price-calculator';

export interface Root {
    dealersInfo: DealersInfo[];
    modelsInfo: ModelsInfo[];
}

export interface DealersInfo {
    country: string;
    countryValues: CountryValue[];
}

export interface CountryValue {
    province: string;
    provinceValues: ProvinceValue[];
}

export interface ProvinceValue {
    town: string;
    dealers: Dealer[];
}

export interface Dealer {
    labourCost: number;
    dealerName: string;
    dealerNameOL: string;
    dealerCode: string;
}

export interface ModelsInfo {
    nameplateName: string;
    models: Model[];
}

export interface Model {
    transmission: string;
    modelYear: string;
    engineSize: string;
    variantName: string;
    serviceIntervals: ServiceInterval[];
}

export interface ServiceInterval {
    yearsFromPurchaseAndDistance: string;
}

export interface ImgSpcDealerModelServiceDataZA {
    country: string;
    province: string;
    town: string;
    dealerCode: string;
    dealerName: string;
    nameplateName: string;
    engineSize: string;
    modelYear: string;
    derivativeName: string;
    serviceInterval: string;
    serviceIntervalForApi: string;
    onlyIOLM: boolean;
    oilChangeIOLM: boolean;
    timingBelt: boolean;
}

interface ImgSpcDealersModelZaProps {
    fetchImgSpcDataForDerivative: (
        resultsForDerivativeParams: ResultsForDerivativeParams
    ) => Promise<void>;
    imgSpcDerivativeData: ImgSpcResultsForDerivativeApiDataData | null;
    setView: React.Dispatch<React.SetStateAction<ComponentType>>;
    updateSelectedDetails: (
        details: Partial<ImgMaintenanceServiceCost>
    ) => void;
    imgSpcDealerModelData: ImgSpcDealerModelDetailsApiDataZAF;
    resultsForDerivativeApiStatus: ApiResponseStatus;
    fieldsLabel?: ImgSpcFieldLabelsContentFragment;
    headersLabel?: ImgSpcHeadingContentFragment;
    iolmLabel?: ImgSpcIolmContentFragment;
    zaLabel?: ImgSpcZaContentFragment;
    setSelectedCountry: (country: string) => void;
    timingBeltChecked: boolean;
    onTimingBeltChange: (checked: boolean) => void;
    onlyIOLMChecked: boolean;
    onOnlyIOLMChange: (checked: boolean) => void;
    oilChangeIOLMChecked: boolean;
    oilChangeIOLMChange: (checked: boolean) => void;
}

function ImgSpcDealersModelZa({
    fetchImgSpcDataForDerivative,
    imgSpcDerivativeData,
    setView,
    resultsForDerivativeApiStatus,
    updateSelectedDetails,
    imgSpcDealerModelData,
    fieldsLabel,
    headersLabel,
    iolmLabel,
    zaLabel,
    setSelectedCountry,
    timingBeltChecked,
    onTimingBeltChange,
    onlyIOLMChecked,
    onOnlyIOLMChange,
    oilChangeIOLMChecked,
    oilChangeIOLMChange,
}: Readonly<ImgSpcDealersModelZaProps>) {
    const countryOption = 'South Africa';
    const countryList = useMemo(() => {
        return imgSpcDealerModelData.dealersInfo.map(country => ({
            label: country.country,
            value: country.country,
        }));
    }, [imgSpcDealerModelData]);

    const handleTimingBeltCheckedChange = useCallback(
        (checked: boolean) => {
            onTimingBeltChange(checked);
        },
        [onTimingBeltChange]
    );

    const handleOnlyIOLMCheckedChange = useCallback(
        (checked: boolean) => {
            onOnlyIOLMChange(checked);
        },
        [onOnlyIOLMChange]
    );

    const handleOilChangeIOLMCheckedChange = useCallback(
        (checked: boolean) => {
            oilChangeIOLMChange(checked);
        },
        [oilChangeIOLMChange]
    );

    const getInitialCountry = () => {
        const initialCountry = countryList?.find(
            country => country.value === countryOption
        );
        return initialCountry ? initialCountry.value : 'South Africa';
    };
    const [
        imgSpcDealerModelServiceDataZA,
        setImgSpcDealerModelServiceDataZA,
    ] = useState<ImgSpcDealerModelServiceDataZA>({
        country: getInitialCountry() || 'South Africa',
        province: '',
        town: '',
        dealerCode: '',
        dealerName: '',
        nameplateName: '',
        engineSize: '',
        modelYear: '',
        derivativeName: '',
        serviceInterval: '',
        serviceIntervalForApi: '',
        onlyIOLM: false,
        oilChangeIOLM: true,
        timingBelt: false,
    });

    const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedCountry = e.target.value;
        setImgSpcDealerModelServiceDataZA(prevState => ({
            ...prevState,
            country: selectedCountry,
            province: '',
            town: '',
            dealerCode: '',
        }));
        setSelectedCountry(selectedCountry);
    };

    const handleImgSpcDropdownChange = (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        if (name === 'country') {
            handleCountryChange(e);
            return;
        }
        setImgSpcDealerModelServiceDataZA(prevState => {
            const newState = { ...prevState, [name]: value };

            if (name === 'country') {
                newState.province = '';
                newState.town = '';
                newState.dealerCode = '';
            } else if (name === 'province') {
                newState.town = '';
                newState.dealerCode = '';
            } else if (name === 'town') {
                newState.dealerCode = '';
            } else if (name === 'nameplateName') {
                newState.engineSize = '';
                newState.modelYear = '';
                newState.derivativeName = '';
                newState.serviceInterval = '';
                newState.serviceIntervalForApi = '';
            } else if (name === 'engineSize') {
                newState.modelYear = '';
                newState.derivativeName = '';
                newState.serviceInterval = '';
            } else if (name === 'modelYear') {
                newState.derivativeName = '';
                newState.serviceInterval = '';
                newState.serviceIntervalForApi = '';
            } else if (name === 'variant') {
                newState.serviceInterval = '';
                newState.serviceIntervalForApi = '';
            } else if (name === 'dealerCode') {
                const selectedDealer = dealerDropdownData.find(
                    dealer => dealer.value === value
                );
                newState.dealerName = selectedDealer
                    ? selectedDealer.label
                    : '';
            }

            return newState;
        });
    };

    useEffect(() => {
        setImgSpcDealerModelServiceDataZA(prev => ({
            ...prev,
            serviceIntervalForApi: prev.onlyIOLM ? '' : prev.serviceInterval,
        }));
    }, [
        imgSpcDealerModelServiceDataZA.serviceInterval,
        imgSpcDealerModelServiceDataZA.onlyIOLM,
    ]);

    console.log({
        imgSpcDropdownDataZA: imgSpcDealerModelServiceDataZA,
    });

    const selectedCountryData = useMemo(() => {
        return imgSpcDealerModelData?.dealersInfo.find(
            country =>
                country.country === imgSpcDealerModelServiceDataZA.country
        );
    }, [imgSpcDealerModelServiceDataZA.country, imgSpcDealerModelData]);

    const provinceDropdownDataSA = useMemo(() => {
        if (
            !selectedCountryData ||
            imgSpcDealerModelServiceDataZA.country !== 'South Africa'
        )
            return [];

        return selectedCountryData.countryValues
            .map(province => ({
                label: province.province || 'Default Province',
                value: province.province,
            }))
            .filter(item => item.value);
    }, [selectedCountryData, imgSpcDealerModelServiceDataZA.country]);

    const cityDropdownData = useMemo(() => {
        if (!selectedCountryData) return [];

        if (imgSpcDealerModelServiceDataZA.country === 'South Africa') {
            if (!imgSpcDealerModelServiceDataZA.province) return [];

            const provinceData = selectedCountryData.countryValues.find(
                province =>
                    province.province ===
                    imgSpcDealerModelServiceDataZA.province
            );

            if (!provinceData) return [];

            return provinceData?.provinceValues?.map(town => ({
                label: town.town,
                value: town.town,
            }));
        } else if (imgSpcDealerModelServiceDataZA.country === 'Namibia') {
            const namibiaData = selectedCountryData.countryValues[0];

            if (!namibiaData) return [];

            return namibiaData?.provinceValues?.map(town => ({
                label: town.town,
                value: town.town,
            }));
        }

        return [];
    }, [
        selectedCountryData,
        imgSpcDealerModelServiceDataZA.country,
        imgSpcDealerModelServiceDataZA.province,
    ]);

    const dealerDropdownData = useMemo(() => {
        if (!selectedCountryData) return [];

        if (
            imgSpcDealerModelServiceDataZA.country === 'South Africa' ||
            imgSpcDealerModelServiceDataZA.country === 'Namibia'
        ) {
            if (!imgSpcDealerModelServiceDataZA.town) return [];

            const provinceData =
                imgSpcDealerModelServiceDataZA.country === 'South Africa'
                    ? selectedCountryData.countryValues.find(
                          p =>
                              p.province ===
                              imgSpcDealerModelServiceDataZA.province
                      )
                    : selectedCountryData.countryValues[0];

            if (!provinceData) return [];

            const townData = provinceData?.provinceValues?.find(
                town => town.town === imgSpcDealerModelServiceDataZA.town
            );

            if (!townData) return [];

            return townData?.dealers?.map(dealer => ({
                label: dealer.dealerName,
                value: dealer.dealerCode,
                data: dealer,
            }));
        } else if (imgSpcDealerModelServiceDataZA.country === 'Swaziland') {
            const allDealers = selectedCountryData.countryValues
                .flatMap(province => province.provinceValues)
                .flatMap(town => town?.dealers);

            return allDealers.map(dealer => ({
                label: dealer?.dealerName,
                value: dealer?.dealerCode,
                data: dealer,
            }));
        }

        return [];
    }, [
        selectedCountryData,
        imgSpcDealerModelServiceDataZA.country,
        imgSpcDealerModelServiceDataZA.province,
        imgSpcDealerModelServiceDataZA.town,
    ]);

    const vehicleDropdownData = useMemo(() => {
        return imgSpcDealerModelData?.modelsInfo.map(model => ({
            label: model.nameplateName,
            value: model.nameplateName,
        }));
    }, [imgSpcDealerModelData]);

    const selectedVehicleData = useMemo(() => {
        if (!imgSpcDealerModelServiceDataZA.nameplateName) return null;

        return imgSpcDealerModelData?.modelsInfo.find(
            model =>
                model.nameplateName ===
                imgSpcDealerModelServiceDataZA.nameplateName
        );
    }, [imgSpcDealerModelServiceDataZA.nameplateName, imgSpcDealerModelData]);

    const engineSizeDropdownData = useMemo(() => {
        if (!selectedVehicleData) return [];

        const uniqueEngineSizes = new Set(
            selectedVehicleData.models.map(model => model.engineSize)
        );

        return Array.from(uniqueEngineSizes).map(size => ({
            label: size,
            value: size,
        }));
    }, [selectedVehicleData]);

    const modelYearDropdownData = useMemo(() => {
        if (!selectedVehicleData || !imgSpcDealerModelServiceDataZA.engineSize)
            return [];

        const filteredModels = selectedVehicleData.models.filter(
            model =>
                model.engineSize === imgSpcDealerModelServiceDataZA.engineSize
        );

        const uniqueModelYears = new Set(
            filteredModels.map(model => model.modelYear)
        );

        return Array.from(uniqueModelYears).map(year => ({
            label: year,
            value: year,
        }));
    }, [selectedVehicleData, imgSpcDealerModelServiceDataZA.engineSize]);

    const serviceIntervalDropdownData = useMemo(() => {
        if (
            !selectedVehicleData ||
            !imgSpcDealerModelServiceDataZA.engineSize ||
            !imgSpcDealerModelServiceDataZA.modelYear
        )
            return [];
        const selectedModels = selectedVehicleData.models.filter(
            model =>
                model.engineSize ===
                    imgSpcDealerModelServiceDataZA.engineSize &&
                model.modelYear === imgSpcDealerModelServiceDataZA.modelYear
        );

        if (selectedModels.length === 0) return [];
        const selectedModel = selectedModels[0];

        const formatServiceInterval = (text: string) => {
            return text.replace(
                /\b(\d{4,})(?=\s*(?:Kms?|Miles?|Km|Mi|\s|$))/gi,
                match => {
                    return parseInt(match, 10).toLocaleString('en-US');
                }
            );
        };

        return selectedModel.serviceIntervals.map(interval => ({
            label: formatServiceInterval(interval.yearsFromPurchaseAndDistance),
            value: interval.yearsFromPurchaseAndDistance,
        }));
    }, [
        selectedVehicleData,
        imgSpcDealerModelServiceDataZA.engineSize,
        imgSpcDealerModelServiceDataZA.modelYear,
    ]);

    useEffect(() => {
        if (
            selectedVehicleData &&
            imgSpcDealerModelServiceDataZA.engineSize &&
            imgSpcDealerModelServiceDataZA.modelYear
        ) {
            const selectedModels = selectedVehicleData.models.filter(
                model =>
                    model.engineSize ===
                        imgSpcDealerModelServiceDataZA.engineSize &&
                    model.modelYear === imgSpcDealerModelServiceDataZA.modelYear
            );

            if (selectedModels.length > 0) {
                const selectedModel = selectedModels[0];
                setImgSpcDealerModelServiceDataZA(prev => ({
                    ...prev,
                    derivativeName: selectedModel.variantName,
                    serviceInterval:
                        selectedModel.serviceIntervals[0]
                            .yearsFromPurchaseAndDistance,
                    serviceIntervalForApi:
                        selectedModel.serviceIntervals[0]
                            .yearsFromPurchaseAndDistance,
                }));
            }
        }
    }, [
        selectedVehicleData,
        imgSpcDealerModelServiceDataZA.engineSize,
        imgSpcDealerModelServiceDataZA.modelYear,
    ]);

    const handleSouthAfricaSelections = useCallback(() => {
        if (
            provinceDropdownDataSA.length === 1 &&
            !imgSpcDealerModelServiceDataZA.province
        ) {
            setImgSpcDealerModelServiceDataZA(prev => ({
                ...prev,
                province: provinceDropdownDataSA[0].value || '',
            }));
        }

        if (
            cityDropdownData?.length === 1 &&
            !imgSpcDealerModelServiceDataZA.town &&
            imgSpcDealerModelServiceDataZA.province
        ) {
            setImgSpcDealerModelServiceDataZA(prev => ({
                ...prev,
                town: cityDropdownData[0].value || '',
            }));
        }
    }, [
        provinceDropdownDataSA,
        cityDropdownData,
        imgSpcDealerModelServiceDataZA.province,
        imgSpcDealerModelServiceDataZA.town,
    ]);

    const handleNamibiaSelections = useCallback(() => {
        if (
            cityDropdownData?.length === 1 &&
            !imgSpcDealerModelServiceDataZA.town
        ) {
            setImgSpcDealerModelServiceDataZA(prev => ({
                ...prev,
                town: cityDropdownData[0].value || '',
            }));
        }
    }, [cityDropdownData, imgSpcDealerModelServiceDataZA.town]);

    const autoSelectDealer = useCallback(() => {
        const shouldAutoselectDealer =
            dealerDropdownData.length === 1 &&
            !imgSpcDealerModelServiceDataZA.dealerCode &&
            ((['South Africa', 'Namibia'].includes(
                imgSpcDealerModelServiceDataZA.country
            ) &&
                imgSpcDealerModelServiceDataZA.town) ||
                imgSpcDealerModelServiceDataZA.country === 'Swaziland');
        if (shouldAutoselectDealer) {
            setImgSpcDealerModelServiceDataZA(prev => ({
                ...prev,
                dealerCode: dealerDropdownData[0].value || '',
                dealerName: dealerDropdownData[0].label || '',
            }));
        }
    }, [
        dealerDropdownData,
        imgSpcDealerModelServiceDataZA.country,
        imgSpcDealerModelServiceDataZA.town,
        imgSpcDealerModelServiceDataZA.dealerCode,
    ]);

    const autoSelectVehicleDetails = useCallback(() => {
        if (
            engineSizeDropdownData.length === 1 &&
            !imgSpcDealerModelServiceDataZA.engineSize &&
            imgSpcDealerModelServiceDataZA.nameplateName
        ) {
            setImgSpcDealerModelServiceDataZA(prev => ({
                ...prev,
                engineSize: engineSizeDropdownData[0].value,
            }));
        }

        if (
            modelYearDropdownData.length === 1 &&
            !imgSpcDealerModelServiceDataZA.modelYear &&
            imgSpcDealerModelServiceDataZA.engineSize
        ) {
            setImgSpcDealerModelServiceDataZA(prev => ({
                ...prev,
                modelYear: modelYearDropdownData[0].value,
            }));
        }
    }, [
        engineSizeDropdownData,
        modelYearDropdownData,
        imgSpcDealerModelServiceDataZA.nameplateName,
        imgSpcDealerModelServiceDataZA.engineSize,
        imgSpcDealerModelServiceDataZA.modelYear,
    ]);

    useEffect(() => {
        if (imgSpcDealerModelServiceDataZA.country === 'South Africa') {
            handleSouthAfricaSelections();
        } else if (imgSpcDealerModelServiceDataZA.country === 'Namibia') {
            handleNamibiaSelections();
        }
        autoSelectDealer();
        autoSelectVehicleDetails();
    }, [
        imgSpcDealerModelServiceDataZA.country,
        handleSouthAfricaSelections,
        handleNamibiaSelections,
        autoSelectDealer,
        autoSelectVehicleDetails,
    ]);

    const areAllMandatoryFieldsSelected = () => {
        const commonFieldsSelected =
            imgSpcDealerModelServiceDataZA.country &&
            imgSpcDealerModelServiceDataZA.dealerCode &&
            imgSpcDealerModelServiceDataZA.nameplateName &&
            imgSpcDealerModelServiceDataZA.engineSize &&
            imgSpcDealerModelServiceDataZA.modelYear;

        if (imgSpcDealerModelServiceDataZA.country === 'South Africa') {
            return (
                commonFieldsSelected &&
                imgSpcDealerModelServiceDataZA.province &&
                imgSpcDealerModelServiceDataZA.town
            );
        } else if (imgSpcDealerModelServiceDataZA.country === 'Namibia') {
            return commonFieldsSelected && imgSpcDealerModelServiceDataZA.town;
        } else if (imgSpcDealerModelServiceDataZA.country === 'Swaziland') {
            return commonFieldsSelected;
        }

        return false;
    };

    useEffect(() => {
        if (areAllMandatoryFieldsSelected()) {
            fetchImgSpcDataForDerivative({
                countryCode: 'ZAF',
                isServiceApplicable: '1',
                dealerCode: imgSpcDealerModelServiceDataZA.dealerCode,
                engineSize: imgSpcDealerModelServiceDataZA.engineSize,
                derivativeName: imgSpcDealerModelServiceDataZA.derivativeName,
            });
        }
    }, [
        imgSpcDealerModelServiceDataZA.derivativeName,
        imgSpcDealerModelServiceDataZA.engineSize,
        imgSpcDealerModelServiceDataZA.dealerCode,
        fetchImgSpcDataForDerivative,
    ]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        console.log('inside handle submit');
        e.preventDefault();
        console.time('API Call');
        await fetchImgSpcDataForDerivative({
            countryCode: 'ZAF',
            isServiceApplicable: '1',
            dealerCode: imgSpcDealerModelServiceDataZA.dealerCode,
            engineSize: imgSpcDealerModelServiceDataZA.engineSize,
            derivativeName: imgSpcDealerModelServiceDataZA.derivativeName,
            ...(imgSpcDealerModelServiceDataZA.serviceIntervalForApi
                ? {
                      serviceInterval:
                          imgSpcDealerModelServiceDataZA.serviceIntervalForApi,
                  }
                : {}),
            ...(imgSpcDealerModelServiceDataZA.oilChangeIOLM || onlyIOLMChecked
                ? { nextGenOilPartNum: 'NEXTGENOIL' }
                : {}),
            ...(onlyIOLMChecked ? { nextGenConsPartNum: 'CONS' } : {}),
        });
        console.timeEnd('API Call');
        if (
            imgSpcDerivativeData &&
            resultsForDerivativeApiStatus === API_RESPONSE.SUCCESS
        ) {
            if (imgSpcDealerModelServiceDataZA.onlyIOLM) {
                console.log(
                    imgSpcDerivativeData.results[0].iolmJson?.iolmParts
                );
            } else {
                console.log(
                    'imgSpcDerivativeData',
                    imgSpcDerivativeData.results[0]
                );
            }
            const nameplateName = imgSpcDealerModelServiceDataZA.nameplateName;
            const engineSize = imgSpcDealerModelServiceDataZA.engineSize;
            const modelYear = imgSpcDealerModelServiceDataZA.modelYear;

            const formattedDerivativeName = `${nameplateName} / ${engineSize} / ${modelYear}`;
            updateSelectedDetails({
                dealerName: imgSpcDealerModelServiceDataZA.dealerName,
                derivativeName: formattedDerivativeName,
                serviceInterval: imgSpcDealerModelServiceDataZA.serviceInterval,
            });
            setView(ComponentType.ImgSpcServiceCost);
        }
    };

    return (
        <form className="img-spc-dealers-model-za" onSubmit={handleSubmit}>
            <div className="img-spc-dropdown-section">
                <div className="img-spc-dropdown">
                    <FMDropdown
                        header={fieldsLabel?.countryFieldLabel}
                        placeholder={fieldsLabel?.countryPlaceHolderText}
                        ariaLabel={
                            fieldsLabel?.countryPlaceHolderText ??
                            'Please Select Country'
                        }
                        options={countryList}
                        value={imgSpcDealerModelServiceDataZA.country}
                        name="country"
                        onChange={handleImgSpcDropdownChange}
                        renderOwnOption={(options?: string[] | Option[]) => {
                            const newOptions = (options ?? []) as Option[];
                            return (
                                <>
                                    <option value="">
                                        {fieldsLabel?.countryPlaceHolderText}
                                    </option>
                                    {newOptions.map(option => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </>
                            );
                        }}
                    />
                </div>

                {imgSpcDealerModelServiceDataZA.country === 'South Africa' && (
                    <div className="img-spc-dropdown">
                        <FMDropdown
                            header={fieldsLabel?.provinceStateFieldLabel}
                            disabled={provinceDropdownDataSA.length === 0}
                            value={imgSpcDealerModelServiceDataZA.province}
                            name="province"
                            onChange={handleImgSpcDropdownChange}
                            options={provinceDropdownDataSA as Option[]}
                            renderOwnOption={(
                                options?: string[] | Option[]
                            ) => {
                                const newOptions = (options ?? []) as Option[];
                                return (
                                    <>
                                        <option value="">
                                            {
                                                fieldsLabel?.provinceStatePlaceHolderText
                                            }
                                        </option>
                                        {newOptions.map(option => (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </>
                                );
                            }}
                            placeholder={
                                fieldsLabel?.provinceStatePlaceHolderText
                            }
                            ariaLabel={
                                fieldsLabel?.provinceStatePlaceHolderText ??
                                'Please Select Province'
                            }
                        />
                    </div>
                )}
                {(imgSpcDealerModelServiceDataZA.country === 'South Africa' ||
                    imgSpcDealerModelServiceDataZA.country === 'Namibia') && (
                    <div className="img-spc-dropdown">
                        <FMDropdown
                            header={fieldsLabel?.cityTownFieldLabel}
                            disabled={
                                (imgSpcDealerModelServiceDataZA.country ===
                                    'South Africa' &&
                                    !imgSpcDealerModelServiceDataZA.province) ||
                                cityDropdownData?.length === 0
                            }
                            value={imgSpcDealerModelServiceDataZA.town}
                            name="town"
                            onChange={handleImgSpcDropdownChange}
                            options={cityDropdownData as Option[]}
                            renderOwnOption={(
                                options?: string[] | Option[]
                            ) => {
                                const newOptions = (options ?? []) as Option[];
                                return (
                                    <>
                                        <option value="">
                                            {
                                                fieldsLabel?.cityTownPlaceHolderText
                                            }
                                        </option>
                                        {newOptions.map(option => (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </>
                                );
                            }}
                            placeholder={fieldsLabel?.cityTownPlaceHolderText}
                            ariaLabel={
                                fieldsLabel?.cityTownPlaceHolderText ??
                                'Please Select City / Town'
                            }
                        />
                    </div>
                )}

                <div className="img-spc-dropdown">
                    <FMDropdown
                        header={fieldsLabel?.preferredDealerLabel}
                        disabled={
                            (imgSpcDealerModelServiceDataZA.country ===
                                'South Africa' &&
                                !imgSpcDealerModelServiceDataZA.town) ||
                            (imgSpcDealerModelServiceDataZA.country ===
                                'Namibia' &&
                                !imgSpcDealerModelServiceDataZA.town) ||
                            !imgSpcDealerModelServiceDataZA.country ||
                            dealerDropdownData.length === 0
                        }
                        value={imgSpcDealerModelServiceDataZA.dealerCode}
                        name="dealerCode"
                        onChange={handleImgSpcDropdownChange}
                        options={dealerDropdownData as Option[]}
                        renderOwnOption={(options?: string[] | Option[]) => {
                            const newOptions = (options ?? []) as Option[];
                            return (
                                <>
                                    <option value="" disabled selected>
                                        {
                                            fieldsLabel?.preferredDealerPlaceHolderText
                                        }
                                    </option>
                                    {newOptions.map(option => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </>
                            );
                        }}
                        placeholder={
                            fieldsLabel?.preferredDealerPlaceHolderText
                        }
                        ariaLabel={
                            fieldsLabel?.preferredDealerPlaceHolderText ??
                            'Please Select Dealer'
                        }
                    />
                </div>
            </div>
            <div className="img-spc-dropdown-section vehicle-section">
                <div className="img-spc-dropdown">
                    <FMDropdown
                        header={fieldsLabel?.selectYourVehicleLabel}
                        disabled={false}
                        value={imgSpcDealerModelServiceDataZA.nameplateName}
                        name="nameplateName"
                        onChange={handleImgSpcDropdownChange}
                        options={vehicleDropdownData ?? []}
                        renderOwnOption={(options?: string[] | Option[]) => {
                            const newOptions = (options ?? []) as Option[];
                            return (
                                <>
                                    <option value="">
                                        {
                                            fieldsLabel?.selectYourVehiclePlaceHolderText
                                        }
                                    </option>
                                    {newOptions.map(option => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </>
                            );
                        }}
                        placeholder={
                            fieldsLabel?.selectYourVehiclePlaceHolderText
                        }
                        ariaLabel={
                            fieldsLabel?.selectYourVehiclePlaceHolderText ??
                            'Please Select Vehicle'
                        }
                    />
                </div>

                <div className="img-spc-dropdown">
                    <FMDropdown
                        header={fieldsLabel?.engineSizeLabel}
                        disabled={
                            !imgSpcDealerModelServiceDataZA.nameplateName ||
                            engineSizeDropdownData.length === 0
                        }
                        value={imgSpcDealerModelServiceDataZA.engineSize}
                        name="engineSize"
                        onChange={handleImgSpcDropdownChange}
                        options={engineSizeDropdownData as Option[]}
                        renderOwnOption={(options?: string[] | Option[]) => {
                            const newOptions = (options ?? []) as Option[];
                            return (
                                <>
                                    <option value="">
                                        {fieldsLabel?.engineSizePlaceHolderText}
                                    </option>
                                    {newOptions.map(option => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </>
                            );
                        }}
                        placeholder={fieldsLabel?.engineSizePlaceHolderText}
                        ariaLabel={
                            fieldsLabel?.engineSizePlaceHolderText ??
                            'Please Select Engine Size'
                        }
                    />
                </div>

                <div className="img-spc-dropdown">
                    <FMDropdown
                        header={fieldsLabel?.modelYearFieldLabel}
                        disabled={
                            !imgSpcDealerModelServiceDataZA.engineSize ||
                            modelYearDropdownData.length === 0
                        }
                        value={imgSpcDealerModelServiceDataZA.modelYear}
                        name="modelYear"
                        onChange={handleImgSpcDropdownChange}
                        options={modelYearDropdownData as Option[]}
                        renderOwnOption={(options?: string[] | Option[]) => {
                            const newOptions = (options ?? []) as Option[];
                            return (
                                <>
                                    <option value="" disabled>
                                        Please Select Model Year
                                    </option>
                                    {newOptions.map(option => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </>
                            );
                        }}
                        placeholder={fieldsLabel?.modelYearPlaceHolderText}
                        ariaLabel={
                            fieldsLabel?.modelYearPlaceHolderText ??
                            'Please Select Model Year'
                        }
                    />
                </div>
            </div>
            {imgSpcDerivativeData && areAllMandatoryFieldsSelected() && (
                <ImgSpcVehicleServiceZa
                    setImgSpcDealerModelServiceDataZA={
                        setImgSpcDealerModelServiceDataZA
                    }
                    handleImgSpcDropdownChange={handleImgSpcDropdownChange}
                    imgSpcDealerModelServiceDataZA={
                        imgSpcDealerModelServiceDataZA
                    }
                    serviceIntervalDropdownData={serviceIntervalDropdownData}
                    imgSpcDerivativeData={imgSpcDerivativeData}
                    headersLabel={headersLabel}
                    fieldsLabel={fieldsLabel}
                    iolmLabel={iolmLabel}
                    zaLabel={zaLabel}
                    timingBeltChecked={timingBeltChecked}
                    onTimingBeltCheckedChange={handleTimingBeltCheckedChange}
                    onlyIOLMChecked={onlyIOLMChecked}
                    onOnlyIOLMCheckedChange={handleOnlyIOLMCheckedChange}
                    oilChangeIOLMChecked={oilChangeIOLMChecked}
                    onOilChangeIOLMCheckedChange={
                        handleOilChangeIOLMCheckedChange
                    }
                />
            )}
            {resultsForDerivativeApiStatus === API_RESPONSE.FAILURE ? (
                <p>Something bad happened please try again after some time</p>
            ) : null}
        </form>
    );
}

export default ImgSpcDealersModelZa;
